import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface CSVUploaderProps {
  onUpload: (file: File) => void;
  accept?: string;
  className?: string;
  templateHeaders?: string[];
}

export const CSVUploader: React.FC<CSVUploaderProps> = ({
  onUpload,
  accept = '.csv',
  className = '',
  templateHeaders
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileName, setFileName] = useState<string>('');
  const [fileError, setFileError] = useState<string>('');

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files[0]) {
      handleFile(files[0]);
    }
  };

  const handleFile = (file: File) => {
    if (file.type === 'text/csv' || file.name.endsWith('.csv')) {
      setFileName(file.name);
      setFileError('');
      onUpload(file);
    } else {
      setFileName('');
      setFileError(t('content.messages.invalidFileType', { type: 'CSV' }));
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className={className}>
      <div
        className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
          isDragging 
            ? 'border-blue-500 bg-blue-50' 
            : 'border-gray-300 hover:border-gray-400'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileSelect}
          className="hidden"
        />
        
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          stroke="currentColor"
          fill="none"
          viewBox="0 0 48 48"
          aria-hidden="true"
        >
          <path
            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        
        <p className="mt-2 text-sm text-gray-600">
          {fileName ? (
            <span className="font-medium text-green-600">
              {t('content.actions.fileSelected', { name: fileName })}
            </span>
          ) : (
            <>
              <span className="font-medium">{t('content.actions.uploadFile')}</span>
              {' '}{t('common.or')}{' '}
              {t('content.actions.dragDrop')}
            </>
          )}
        </p>
        <p className="text-xs text-gray-500 mt-1">CSV {t('content.actions.filesOnly')}</p>
        {fileError ? <p className="text-xs text-red-600 mt-2">{fileError}</p> : null}
        {templateHeaders && (
          <div className="mt-3 p-2 bg-gray-50 rounded text-left">
            <p className="text-xs text-gray-600 font-medium mb-1">Expected headers:</p>
            <p className="text-xs text-gray-500">{templateHeaders.join(', ')}</p>
          </div>
        )}
      </div>
    </div>
  );
};
